/**
 * Knowledge bases, libraries, and books load from GET /api/knowledge/registry (Dynamo).
 * localStorage is a read-through cache only; One and Agents share the org API, not cookies.
 */

import { apiGet } from "@/lib/apiClient";
import type { KnowledgeBaseRecord } from "@/lib/knowledgeBaseRegistry";
import {
  KNOWLEDGE_BASES_CHANGED,
  DEFAULT_KNOWLEDGE_BASE_ID,
  knowledgeBaseNameIsIdFallback,
  resolveKnowledgeBaseDisplayName,
} from "@/lib/knowledgeBaseRegistry";
import type { KnowledgeCustomBook } from "@/lib/knowledgeBookRegistry";
import { KNOWLEDGE_CUSTOM_BOOKS_CHANGED } from "@/lib/knowledgeBookRegistry";
import type { KnowledgeCustomLibrary } from "@/lib/knowledgeLibraryRegistry";
import { KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED } from "@/lib/knowledgeLibraryRegistry";
import {
  pinnedKnowledgeBaseActiveId,
  reconcileKnowledgeBaseActivePin,
} from "@/lib/knowledgeActiveSelection";
import {
  apiCreateKnowledgeBase,
  apiCreateKnowledgeBook,
  apiCreateKnowledgeLibrary,
  apiSetActiveKnowledgeBase,
  fetchKnowledgeRegistry,
  type KnowledgeRegistryPayload,
} from "@/lib/knowledgeRegistryApi";

const SYNC_TS_PREFIX = "atw-one.knowledge.regSyncTs";
const BASES_PREFIX = "atw-one.knowledge.bases";
const ACTIVE_PREFIX = "atw-one.knowledge.activeBase";
const LIB_PREFIX = "atw-one.knowledge.customLibraries";
const BOOK_PREFIX = "atw-one.knowledge.customBooks";
const KB_MEMBERSHIP_PREFIX = "kb:";

export type KnowledgeOrgRegistrySnapshot = {
  bases: KnowledgeBaseRecord[];
  activeId: string;
  librariesByKb: Record<string, KnowledgeCustomLibrary[]>;
  booksByKb: Record<string, KnowledgeCustomBook[]>;
  /** Tombstoned base ids from the API (and local deletes before refresh). */
  deletedBaseIds?: string[];
};

let registryLoadError: string | null = null;
let registryLoadedAt: string | null = null;

function orgStorageKey(orgId: string | null | undefined): string {
  return (orgId || "default").trim() || "default";
}

function syncTsKey(orgId: string | null | undefined): string {
  return `${SYNC_TS_PREFIX}.${orgStorageKey(orgId)}`;
}

function basesKey(orgId: string | null | undefined): string {
  return `${BASES_PREFIX}.${orgStorageKey(orgId)}`;
}

function activeKey(orgId: string | null | undefined): string {
  return `${ACTIVE_PREFIX}.${orgStorageKey(orgId)}`;
}

function librariesKey(orgId: string | null | undefined, kbId: string): string {
  return `${LIB_PREFIX}.${orgStorageKey(orgId)}.${kbId || DEFAULT_KNOWLEDGE_BASE_ID}`;
}

function booksKey(orgId: string | null | undefined, kbId: string): string {
  return `${BOOK_PREFIX}.${orgStorageKey(orgId)}.${kbId || DEFAULT_KNOWLEDGE_BASE_ID}`;
}

function sortBases(bases: KnowledgeBaseRecord[]): KnowledgeBaseRecord[] {
  return [...bases].sort((a, b) => a.name.localeCompare(b.name));
}

function parseKbMembershipId(key: string): string | null {
  if (!key.startsWith(KB_MEMBERSHIP_PREFIX) || key.length <= KB_MEMBERSHIP_PREFIX.length) {
    return null;
  }
  return key.slice(KB_MEMBERSHIP_PREFIX.length).trim() || null;
}

function normalizeDeletedBaseIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const ids = new Set<string>();
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const id = item.trim();
    if (id && id !== DEFAULT_KNOWLEDGE_BASE_ID) ids.add(id);
  }
  return [...ids].sort((a, b) => a.localeCompare(b));
}

function payloadToSnapshot(payload: KnowledgeRegistryPayload): KnowledgeOrgRegistrySnapshot {
  const librariesByKb: Record<string, KnowledgeCustomLibrary[]> = {};
  for (const [kbId, rows] of Object.entries(payload.librariesByBase || {})) {
    if (Array.isArray(rows) && rows.length) librariesByKb[kbId] = rows;
  }
  const booksByKb: Record<string, KnowledgeCustomBook[]> = {};
  for (const [kbId, rows] of Object.entries(payload.booksByBase || {})) {
    if (Array.isArray(rows) && rows.length) booksByKb[kbId] = rows;
  }
  let activeId = (payload.activeBaseId || "").trim();
  const deletedBaseIds = normalizeDeletedBaseIds(payload.deletedBaseIds);
  const deleted = new Set(deletedBaseIds);
  const bases = (payload.bases || []).filter((base) => !deleted.has(base.id));
  if (activeId && !bases.some((base) => base.id === activeId)) {
    activeId = bases[0]?.id || "";
  }
  return { bases, activeId, librariesByKb, booksByKb, deletedBaseIds };
}

function allKbIdsForSnapshot(snap: KnowledgeOrgRegistrySnapshot): string[] {
  const ids = new Set<string>(snap.bases.map((base) => base.id));
  for (const kbId of Object.keys(snap.librariesByKb || {})) ids.add(kbId);
  for (const kbId of Object.keys(snap.booksByKb || {})) ids.add(kbId);
  return [...ids].filter((id) => id && id !== DEFAULT_KNOWLEDGE_BASE_ID);
}

/** Resolve active base when merging API snapshot with local cache (pin and local beat stale remote). */
export function resolveRegistryActiveId(
  remoteActive: string,
  localActive: string,
  bases: KnowledgeBaseRecord[],
  orgId?: string | null,
): string {
  const isValid = (id: string) => {
    const trimmed = (id || "").trim();
    return trimmed.length > 0 && bases.some((base) => base.id === trimmed);
  };

  const pinned = pinnedKnowledgeBaseActiveId(orgId, isValid);
  if (pinned) return pinned;

  const local = (localActive || "").trim();
  const remote = (remoteActive || "").trim();

  if (isValid(local) && isValid(remote) && local !== remote) {
    return local;
  }

  let activeId = remote;
  if (activeId && !isValid(activeId)) {
    activeId = local;
  }
  if (activeId && !isValid(activeId)) {
    activeId = bases[0]?.id || "";
  }
  if (!activeId && isValid(local)) {
    activeId = local;
  }
  return activeId;
}

function preferKnowledgeBaseRecord(
  preferred: KnowledgeBaseRecord,
  other?: KnowledgeBaseRecord,
): KnowledgeBaseRecord {
  if (!other) return preferred;
  const preferredNameBad = knowledgeBaseNameIsIdFallback(preferred.name, preferred.id);
  const otherNameBad = knowledgeBaseNameIsIdFallback(other.name, other.id);
  if (preferredNameBad && !otherNameBad) {
    return {
      ...preferred,
      name: other.name,
      description: preferred.description || other.description,
    };
  }
  if (!preferred.description && other.description) {
    return { ...preferred, description: other.description };
  }
  return preferred;
}

/** Union remote + local by id; remote wins base membership. Local only upgrades shared rows. */
export function mergeRegistrySnapshots(
  remote: KnowledgeOrgRegistrySnapshot,
  local: KnowledgeOrgRegistrySnapshot,
  orgId?: string | null,
): KnowledgeOrgRegistrySnapshot {
  const deletedBaseIds = normalizeDeletedBaseIds([
    ...(remote.deletedBaseIds || []),
    ...(local.deletedBaseIds || []),
  ]);
  const deleted = new Set(deletedBaseIds);
  const baseById = new Map<string, KnowledgeBaseRecord>();
  for (const base of remote.bases) {
    if (deleted.has(base.id)) continue;
    baseById.set(base.id, base);
  }
  // Do not resurrect local-only bases: remote registry is the membership source of truth.
  for (const base of local.bases) {
    if (deleted.has(base.id)) continue;
    const existing = baseById.get(base.id);
    if (!existing) continue;
    baseById.set(base.id, preferKnowledgeBaseRecord(existing, base));
  }
  const bases = sortBases([...baseById.values()]);
  const liveBaseIds = new Set(bases.map((base) => base.id));

  const librariesByKb: Record<string, KnowledgeCustomLibrary[]> = {};
  const allLibKbIds = new Set([
    ...Object.keys(remote.librariesByKb || {}),
    ...Object.keys(local.librariesByKb || {}),
  ]);
  for (const kbId of allLibKbIds) {
    if (deleted.has(kbId) || !liveBaseIds.has(kbId)) continue;
    const byId = new Map<string, KnowledgeCustomLibrary>();
    for (const row of remote.librariesByKb[kbId] || []) byId.set(row.id, row);
    for (const row of local.librariesByKb[kbId] || []) {
      if (!byId.has(row.id)) byId.set(row.id, row);
    }
    const merged = [...byId.values()];
    if (merged.length) librariesByKb[kbId] = merged;
  }

  const booksByKb: Record<string, KnowledgeCustomBook[]> = {};
  const allBookKbIds = new Set([
    ...Object.keys(remote.booksByKb || {}),
    ...Object.keys(local.booksByKb || {}),
  ]);
  for (const kbId of allBookKbIds) {
    if (deleted.has(kbId) || !liveBaseIds.has(kbId)) continue;
    const byKey = new Map<string, KnowledgeCustomBook>();
    for (const row of remote.booksByKb[kbId] || []) {
      byKey.set(row.collectionKey, row);
    }
    for (const row of local.booksByKb[kbId] || []) {
      const existing = byKey.get(row.collectionKey);
      if (!existing) {
        byKey.set(row.collectionKey, row);
        continue;
      }
      if (!existing.description && row.description) {
        byKey.set(row.collectionKey, { ...existing, description: row.description });
      }
    }
    const merged = [...byKey.values()];
    if (merged.length) booksByKb[kbId] = merged;
  }

  const activeId = resolveRegistryActiveId(remote.activeId, local.activeId, bases, orgId);

  return { bases, activeId, librariesByKb, booksByKb, deletedBaseIds };
}

/**
 * Entries present locally but absent from the remote registry payload.
 * When remote already has bases, never re-upload missing bases (delete/tombstone safe).
 * Empty-remote migration may still upload local bases that are not tombstoned.
 */
export function snapshotMissingOnRemote(
  local: KnowledgeOrgRegistrySnapshot,
  remote: KnowledgeOrgRegistrySnapshot,
): KnowledgeOrgRegistrySnapshot {
  const deleted = new Set(
    normalizeDeletedBaseIds([...(remote.deletedBaseIds || []), ...(local.deletedBaseIds || [])]),
  );
  const remoteBaseIds = new Set(remote.bases.map((base) => base.id));
  const remoteHasBases = remote.bases.length > 0;
  const missingBases = remoteHasBases
    ? []
    : local.bases.filter((base) => !remoteBaseIds.has(base.id) && !deleted.has(base.id));

  const librariesByKb: Record<string, KnowledgeCustomLibrary[]> = {};
  const booksByKb: Record<string, KnowledgeCustomBook[]> = {};
  const allowedKbIds = new Set([
    ...remoteBaseIds,
    ...missingBases.map((base) => base.id),
  ]);

  for (const kbId of allKbIdsForSnapshot(local)) {
    if (deleted.has(kbId) || !allowedKbIds.has(kbId)) continue;

    const remoteLibIds = new Set((remote.librariesByKb[kbId] || []).map((row) => row.id));
    const missingLibs = (local.librariesByKb[kbId] || []).filter((row) => !remoteLibIds.has(row.id));
    if (missingLibs.length) librariesByKb[kbId] = missingLibs;

    const remoteBookKeys = new Set(
      (remote.booksByKb[kbId] || []).map((row) => row.collectionKey),
    );
    const missingBooks = (local.booksByKb[kbId] || []).filter(
      (row) => !remoteBookKeys.has(row.collectionKey),
    );
    if (missingBooks.length) booksByKb[kbId] = missingBooks;
  }

  return {
    bases: sortBases(missingBases),
    activeId: local.activeId,
    librariesByKb,
    booksByKb,
    deletedBaseIds: [...deleted],
  };
}

export function applyKnowledgeRegistrySnapshot(
  orgId: string | null | undefined,
  snap: KnowledgeOrgRegistrySnapshot,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(basesKey(orgId), JSON.stringify(snap.bases));
    if (snap.activeId) {
      window.localStorage.setItem(activeKey(orgId), snap.activeId);
    } else {
      window.localStorage.removeItem(activeKey(orgId));
    }
    for (const kbId of allKbIdsForSnapshot(snap)) {
      const libs = snap.librariesByKb[kbId];
      if (libs?.length) {
        window.localStorage.setItem(librariesKey(orgId, kbId), JSON.stringify(libs));
      } else {
        window.localStorage.removeItem(librariesKey(orgId, kbId));
      }
      const books = snap.booksByKb[kbId];
      if (books?.length) {
        window.localStorage.setItem(booksKey(orgId, kbId), JSON.stringify(books));
      } else {
        window.localStorage.removeItem(booksKey(orgId, kbId));
      }
    }
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_BASES_CHANGED));
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED));
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_CUSTOM_BOOKS_CHANGED));
  } catch {
    /* ignore quota */
  }
}

function readLocalSnapshot(orgId: string | null | undefined): KnowledgeOrgRegistrySnapshot {
  if (typeof window === "undefined") {
    return { bases: [], activeId: "", librariesByKb: {}, booksByKb: {} };
  }
  try {
    const basesRaw = window.localStorage.getItem(basesKey(orgId));
    const bases = basesRaw ? (JSON.parse(basesRaw) as KnowledgeBaseRecord[]) : [];
    const activeId = (window.localStorage.getItem(activeKey(orgId)) || "").trim();
    const librariesByKb: Record<string, KnowledgeCustomLibrary[]> = {};
    const booksByKb: Record<string, KnowledgeCustomBook[]> = {};
    for (const kbId of allKbIdsForSnapshot({ bases, activeId, librariesByKb, booksByKb })) {
      const libRaw = window.localStorage.getItem(librariesKey(orgId, kbId));
      if (libRaw) librariesByKb[kbId] = JSON.parse(libRaw) as KnowledgeCustomLibrary[];
      const bookRaw = window.localStorage.getItem(booksKey(orgId, kbId));
      if (bookRaw) booksByKb[kbId] = JSON.parse(bookRaw) as KnowledgeCustomBook[];
    }
    return { bases, activeId, librariesByKb, booksByKb };
  } catch {
    return { bases: [], activeId: "", librariesByKb: {}, booksByKb: {} };
  }
}

function snapshotsEqual(a: KnowledgeOrgRegistrySnapshot, b: KnowledgeOrgRegistrySnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function snapshotHasRegistryContent(snap: KnowledgeOrgRegistrySnapshot): boolean {
  if (snap.bases.length > 0) return true;
  for (const rows of Object.values(snap.librariesByKb)) {
    if (rows?.length) return true;
  }
  for (const rows of Object.values(snap.booksByKb)) {
    if (rows?.length) return true;
  }
  return false;
}

async function pushSnapshotToApi(
  snap: KnowledgeOrgRegistrySnapshot,
  options?: { setActive?: boolean },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const baseIds = new Set(snap.bases.map((base) => base.id));
  for (const kbId of allKbIdsForSnapshot(snap)) {
    if (!baseIds.has(kbId)) {
      const created = await apiCreateKnowledgeBase({
        id: kbId,
        name: resolveKnowledgeBaseDisplayName({ id: kbId }),
      });
      if (!created.ok) {
        return { ok: false, error: created.error || "Could not migrate knowledge base." };
      }
      baseIds.add(kbId);
    }
  }
  for (const base of snap.bases) {
    const created = await apiCreateKnowledgeBase({
      id: base.id,
      name: resolveKnowledgeBaseDisplayName({ id: base.id, name: base.name }),
      description: base.description,
    });
    if (!created.ok) {
      return { ok: false, error: created.error || "Could not migrate knowledge base." };
    }
  }
  for (const kbId of allKbIdsForSnapshot(snap)) {
    for (const library of snap.librariesByKb[kbId] || []) {
      const created = await apiCreateKnowledgeLibrary(kbId, {
        id: library.id,
        title: library.title,
        description: library.description,
      });
      if (!created.ok) {
        return { ok: false, error: created.error || "Could not migrate library." };
      }
    }
    for (const book of snap.booksByKb[kbId] || []) {
      const created = await apiCreateKnowledgeBook(kbId, {
        title: book.title,
        description: book.description,
        collectionKey: book.collectionKey,
        libraryId: book.libraryId,
      });
      if (!created.ok) {
        return { ok: false, error: created.error || "Could not migrate book." };
      }
    }
  }
  if (options?.setActive !== false && snap.activeId) {
    const active = await apiSetActiveKnowledgeBase(snap.activeId);
    if (!active.ok) {
      return { ok: false, error: active.error || "Could not migrate active knowledge base." };
    }
  }
  return { ok: true };
}

async function augmentLocalFromArticleKbKeys(
  local: KnowledgeOrgRegistrySnapshot,
  options?: { allowInvent?: boolean; deletedBaseIds?: string[] },
): Promise<KnowledgeOrgRegistrySnapshot> {
  const res = await apiGet<{
    articles?: {
      title?: string;
      collectionKeys: string[];
      createdAt?: string;
      updatedAt?: string;
    }[];
  }>("/api/knowledge/articles?limit=500");
  if (!res.ok || !Array.isArray(res.data?.articles)) return local;

  const deleted = new Set(
    normalizeDeletedBaseIds([...(local.deletedBaseIds || []), ...(options?.deletedBaseIds || [])]),
  );
  const allowInvent = options?.allowInvent === true;
  const byId = new Map(
    local.bases.filter((base) => !deleted.has(base.id)).map((base) => [base.id, base]),
  );
  let changed = false;
  for (const article of res.data.articles) {
    for (const key of article.collectionKeys || []) {
      const id = parseKbMembershipId(key);
      if (!id || id === DEFAULT_KNOWLEDGE_BASE_ID || deleted.has(id)) continue;
      const title = (article.title || "").trim();
      const existing = byId.get(id);
      if (existing) {
        if (
          knowledgeBaseNameIsIdFallback(existing.name, id) &&
          title &&
          !knowledgeBaseNameIsIdFallback(title, id)
        ) {
          changed = true;
          byId.set(id, { ...existing, name: title });
        }
        continue;
      }
      // Only invent during empty-remote migration. Never reseed deleted or unknown bases
      // from leftover kb: keys / draft anchors once a registry already exists.
      if (!allowInvent) continue;
      changed = true;
      byId.set(id, {
        id,
        name: resolveKnowledgeBaseDisplayName({ id, articleTitle: title }),
        createdAt: article.createdAt || article.updatedAt || new Date().toISOString(),
      });
    }
  }
  if (!changed) return local;
  return {
    ...local,
    bases: sortBases([...byId.values()]),
    deletedBaseIds: [...deleted],
  };
}

/** Load org registry from API and refresh the local cache. */
export async function refreshKnowledgeRegistryFromApi(
  orgId: string | null | undefined,
  options?: { skipLocalPush?: boolean },
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!(orgId || "").trim()) return false;

  let localBefore = readLocalSnapshot(orgId);
  const res = await fetchKnowledgeRegistry();
  if (!res.ok) {
    registryLoadError = res.error;
    return false;
  }
  registryLoadError = null;

  let remoteSnap = payloadToSnapshot(res.data);
  let loadedPayload = res.data;
  const remoteEmpty = !snapshotHasRegistryContent(remoteSnap);

  if (!options?.skipLocalPush) {
    localBefore = await augmentLocalFromArticleKbKeys(localBefore, {
      // Invent from article kb: keys only while migrating an empty remote registry.
      allowInvent: remoteEmpty,
      deletedBaseIds: remoteSnap.deletedBaseIds,
    });
  }

  const localHasContent = snapshotHasRegistryContent(localBefore);
  let didWriteRemote = false;

  if (!options?.skipLocalPush) {
    if (remoteEmpty && localHasContent) {
      const migrated = await pushSnapshotToApi(localBefore, { setActive: true });
      if (!migrated.ok) {
        registryLoadError = migrated.error;
        return false;
      }
      didWriteRemote = true;
    } else {
      const missingOnRemote = snapshotMissingOnRemote(localBefore, remoteSnap);
      if (snapshotHasRegistryContent(missingOnRemote)) {
        const migrated = await pushSnapshotToApi(missingOnRemote, { setActive: false });
        if (!migrated.ok) {
          registryLoadError = migrated.error;
          return false;
        }
        didWriteRemote = true;
      }
    }
  }

  if (didWriteRemote) {
    const refetch = await fetchKnowledgeRegistry();
    if (!refetch.ok) {
      registryLoadError = refetch.error;
      return false;
    }
    loadedPayload = refetch.data;
    remoteSnap = payloadToSnapshot(refetch.data);
  }

  const localAtMerge = readLocalSnapshot(orgId);
  // After deletes, trust the remote registry so local cache cannot resurrect removed rows.
  const merged = options?.skipLocalPush
    ? {
        ...remoteSnap,
        activeId: resolveRegistryActiveId(
          remoteSnap.activeId || "",
          localAtMerge.activeId || "",
          remoteSnap.bases,
          orgId,
        ),
      }
    : mergeRegistrySnapshots(remoteSnap, localAtMerge, orgId);
  reconcileKnowledgeBaseActivePin(orgId, remoteSnap.activeId);
  if (!snapshotHasRegistryContent(merged) && remoteEmpty && localHasContent && !options?.skipLocalPush) {
    registryLoadError = "Knowledge registry stayed empty after migrating local data.";
    return false;
  }

  registryLoadedAt = loadedPayload.updatedAt || new Date().toISOString();
  const changed = !snapshotsEqual(localBefore, merged);
  applyKnowledgeRegistrySnapshot(orgId, merged);
  try {
    window.localStorage.setItem(syncTsKey(orgId), String(Date.now()));
  } catch {
    /* ignore */
  }
  return changed;
}

/** @deprecated Use refreshKnowledgeRegistryFromApi (async). Kept for call-site compatibility. */
export function syncKnowledgeRegistryForOrg(orgId: string | null | undefined): boolean {
  void refreshKnowledgeRegistryFromApi(orgId);
  return false;
}

/** Mutations persist via API; no cookie publish step. */
export function publishKnowledgeRegistryForOrg(_orgId: string | null | undefined): void {
  /* no-op */
}

export const KNOWLEDGE_REGISTRY_SYNC_NOTE =
  "Knowledge bases, libraries, and books load from your org Knowledge API (same store as articles).";

export function getKnowledgeRegistryCookieSyncWarning(): string | null {
  return registryLoadError;
}

export function knowledgeRegistrySyncBannerText(): string {
  if (registryLoadError) {
    return `Could not load knowledge libraries from the API: ${registryLoadError}`;
  }
  return KNOWLEDGE_REGISTRY_SYNC_NOTE;
}

export function getKnowledgeRegistryLoadedAt(): string | null {
  return registryLoadedAt;
}
